import type { ApiDfsContest } from '../api/dfs.ts';
import { EPL_COMPETITION_ID, LALIGA_COMPETITION_ID } from './featuredContest.ts';

export type ContestLobbyScope = 'combined' | 'epl' | 'laliga';
export type ContestLobbyMode = 'active' | 'upcoming' | 'entered';
export type ContestLobbyState =
  | 'entered_active'
  | 'entered_upcoming'
  | 'active_buildable'
  | 'upcoming';

export interface ContestLobbyChoice {
  contest: ApiDfsContest;
  scope: ContestLobbyScope;
  mode: ContestLobbyMode;
  state: ContestLobbyState;
  featured: boolean;
}

interface ContestCandidate {
  contest: ApiDfsContest;
  scope: ContestLobbyScope;
  temporalState: 'active' | 'upcoming';
  entered: boolean;
  buildable: boolean;
}

const SCOPES: ContestLobbyScope[] = ['combined', 'epl', 'laliga'];
const SCOPE_DISPLAY_ORDER: Record<ContestLobbyScope, number> = {
  combined: 0,
  epl: 1,
  laliga: 2,
};

export const isCombinedEplLaligaContest = (contest: ApiDfsContest): boolean => {
  const competitionIds = new Set(
    (contest.competitions ?? []).map(competition => competition.id),
  );
  return competitionIds.has(EPL_COMPETITION_ID) && competitionIds.has(LALIGA_COMPETITION_ID);
};

export const contestLobbyScope = (contest: ApiDfsContest): ContestLobbyScope | null => {
  if (!contest.is_weekend_contest) return null;
  const competitionIds = (contest.competitions ?? []).map(competition => competition.id);
  if (isCombinedEplLaligaContest(contest)) return 'combined';
  if (competitionIds[0] === EPL_COMPETITION_ID) return 'epl';
  if (competitionIds[0] === LALIGA_COMPETITION_ID) return 'laliga';
  return null;
};

const contestStartMs = (contest: ApiDfsContest): number => {
  const raw = contest.start_time || contest.contest_date;
  const value = raw ? new Date(raw).getTime() : Number.POSITIVE_INFINITY;
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
};

/** Shared list ordering: the combined flagship always leads, then kickoff. */
export const compareContestsCombinedFirst = (
  a: ApiDfsContest,
  b: ApiDfsContest,
): number => {
  const scopeDifference = Number(!isCombinedEplLaligaContest(a))
    - Number(!isCombinedEplLaligaContest(b));
  return scopeDifference || contestStartMs(a) - contestStartMs(b);
};

const contestIsBuildable = (contest: ApiDfsContest, nowMs: number): boolean => {
  if (!contest.last_match_start_time) return true;
  const lastLockMs = new Date(contest.last_match_start_time).getTime();
  return !Number.isFinite(lastLockMs) || lastLockMs > nowMs;
};

const mergeEnteredContest = (
  contest: ApiDfsContest,
  enteredContest: ApiDfsContest | undefined,
): ApiDfsContest => {
  if (!enteredContest) return contest;
  return {
    ...contest,
    ...enteredContest,
    competitions: enteredContest.competitions ?? contest.competitions,
    relevant_match_ids: enteredContest.relevant_match_ids ?? contest.relevant_match_ids,
    start_time: enteredContest.start_time ?? contest.start_time,
    last_match_start_time: enteredContest.last_match_start_time ?? contest.last_match_start_time,
  };
};

const candidateRank = (candidate: ContestCandidate): number => {
  if (candidate.entered && candidate.temporalState === 'active') return 0;
  if (candidate.entered && candidate.temporalState === 'upcoming') return 1;
  if (candidate.temporalState === 'active' && candidate.buildable) return 2;
  if (candidate.temporalState === 'upcoming') return 3;
  return 4;
};

const choicePriority = (choice: ContestLobbyChoice): number => {
  if (choice.state === 'entered_active') return 0;
  if (choice.state === 'entered_upcoming') return 1;
  return 2;
};

/**
 * Reduce the contest feeds to the lobby's three stable weekend choices.
 * Within a competition the soonest-starting live/upcoming contest always
 * leads, so the current weekend can never be hidden by a lineup already
 * entered in a later slate (that lineup stays reachable via the "other open
 * lineups" list). Entered/active status only breaks start-time ties.
 * Globally, the combined flagship always leads the two single leagues.
 * Entered/active state then orders cards within each scope.
 */
export const selectContestLobbyChoices = (
  active: ApiDfsContest[],
  upcoming: ApiDfsContest[],
  entered: ApiDfsContest[],
  nowMs: number = Date.now(),
): ContestLobbyChoice[] => {
  const openEntered = entered.filter(contest => !contest.is_finalized);
  const enteredById = new Map(openEntered.map(contest => [contest.id, contest]));
  const candidatesById = new Map<number, ContestCandidate>();

  const addCandidate = (baseContest: ApiDfsContest, temporalState: 'active' | 'upcoming') => {
    if (baseContest.is_finalized) return;
    const contest = mergeEnteredContest(baseContest, enteredById.get(baseContest.id));
    const scope = contestLobbyScope(contest);
    if (!scope) return;
    const enteredContest = enteredById.has(contest.id) || contest.user_lineup_id != null;
    // A lobby can remain open through kickoff. Promote a stale `upcoming`
    // feed item locally so its badge and CTA do not stay pregame until a
    // navigation or network refresh.
    const effectiveTemporalState = temporalState === 'upcoming' && contestStartMs(contest) <= nowMs
      ? 'active'
      : temporalState;
    const candidate: ContestCandidate = {
      contest,
      scope,
      temporalState: effectiveTemporalState,
      entered: enteredContest,
      buildable: contestIsBuildable(contest, nowMs),
    };
    const existing = candidatesById.get(contest.id);
    if (!existing || effectiveTemporalState === 'active') candidatesById.set(contest.id, candidate);
  };

  active.forEach(contest => addCandidate(contest, 'active'));
  upcoming.forEach(contest => addCandidate(contest, 'upcoming'));

  // Keep a user's open lineup reachable even if one of the catalogue feeds is
  // briefly stale or unavailable. Its kickoff determines the temporal state.
  openEntered.forEach(contest => {
    if (candidatesById.has(contest.id)) return;
    addCandidate(contest, contestStartMs(contest) <= nowMs ? 'active' : 'upcoming');
  });

  const candidates = [...candidatesById.values()];
  const choices = SCOPES.flatMap(scope => {
    const candidate = candidates
      .filter(item => item.scope === scope)
      .filter(item => candidateRank(item) < 4)
      .sort((a, b) => contestStartMs(a.contest) - contestStartMs(b.contest) || candidateRank(a) - candidateRank(b))[0];
    if (!candidate) return [];
    const state: ContestLobbyState = candidate.entered
      ? candidate.temporalState === 'active' ? 'entered_active' : 'entered_upcoming'
      : candidate.temporalState === 'active' ? 'active_buildable' : 'upcoming';
    return [{
      contest: candidate.contest,
      scope,
      state,
      mode: candidate.entered ? 'entered' : candidate.temporalState,
      featured: scope === 'combined',
    } satisfies ContestLobbyChoice];
  });

  return choices.sort((a, b) => {
    const scopeDifference = SCOPE_DISPLAY_ORDER[a.scope] - SCOPE_DISPLAY_ORDER[b.scope];
    if (scopeDifference !== 0) return scopeDifference;
    const priorityDifference = choicePriority(a) - choicePriority(b);
    if (priorityDifference !== 0) return priorityDifference;
    return contestStartMs(a.contest) - contestStartMs(b.contest);
  });
};
